export function formatStat(
  value: any,
  key: string = '',
  prefix: string = ''
): string {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = Number(value);
  if (isNaN(numericValue)) return '-';

  const isPercentage = key.includes('%');

   if (isPercentage) {
    return `${numericValue.toFixed(1)}${prefix}`; // Keep 1 decimal for %
  }

  const absValue = Math.abs(numericValue);
  let formattedValue = '';
  let suffix = '';

  if (absValue >= 1e9) {
    formattedValue = Math.round(numericValue / 1e9).toString();
    suffix = 'B';
  } else if (absValue >= 1e6) {
    formattedValue = Math.round(numericValue / 1e6).toString();
    suffix = 'M';
  } else if (absValue >= 1e3) {
    formattedValue = Math.round(numericValue / 1e3).toString();
    suffix = 'K';
  } else {
    formattedValue = Math.round(numericValue).toString();
  }

  return `${prefix}${formattedValue}${suffix}`;
}

export function formatStatOneDecimal(
  value: any,
  key: string = '',
  prefix: string = ''
): string {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = Number(value);
  if (isNaN(numericValue)) return '-';

  const isPercentage = key.includes('%');

  if (isPercentage) {
    return `${numericValue.toFixed(1)}${prefix}`; // Keep 1 decimal for %
  }

  const absValue = Math.abs(numericValue);
  let formattedValue = '';
  let suffix = '';

  if (absValue === 0) {
    return `${prefix}0`;
  } else if (absValue >= 1e9) {
    formattedValue = (numericValue / 1e9).toFixed(1);
    suffix = 'B';
  } else if (absValue >= 1e6) {
    formattedValue = (numericValue / 1e6).toFixed(1);
    suffix = 'M';
  } else if (absValue >= 1e3) {
    formattedValue = (numericValue / 1e3).toFixed(1);
    suffix = 'K';
  } else {
    formattedValue = (numericValue / 1e3).toFixed(1);
    suffix = 'K';
  }

  return `${prefix}${formattedValue}${suffix}`;
}



export function commaSeparatedValue(value: any): string {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = Number(value);
  if (isNaN(numericValue)) return '-';

  return numericValue.toLocaleString('en-US');
}

export function roundToNearestInteger(value: number): number {
  return Math.round(value);
}

export function formatDecimalWithCommas(value: number | string): string {
  const num = typeof value === 'number' ? value : parseFloat(value as string);
  if (isNaN(num)) return '-';

  // Always round to the nearest whole number and add commas
  return Math.round(num).toLocaleString('en-US');
}

export function oneDecimalWithCommas(value: number | string): string {
  const num = typeof value === 'number' ? value : parseFloat(value as string);
  if (isNaN(num)) return '-';

  return num.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/**
 * Shared numeric formatter for the self-service chart/KPI pipeline: applies a
 * NumberFormat (plain/thousands/percent/currency) and an optional magnitude
 * NumberScale (none/compact/millions/billions) on top. Used both by
 * SelfServiceDataUtilsService (builder preview) and NumberStatsComponent (the
 * published KPI widget) so the two stay in sync.
 */
export function formatSelfServiceNumber(
  value: number,
  numberFormat: string,
  decimalsRaw: number | undefined,
  numberScale: string = 'none',
  currencySymbol: string = '$',
): string {
  if (!Number.isFinite(value)) return '-';
  const decimals = Math.max(0, Math.min(6, Math.floor(decimalsRaw || 0)));
  const isPercent = numberFormat === 'percent';
  const scaledForFormat = isPercent ? value * 100 : value;
  const prefix = numberFormat === 'currency' ? currencySymbol : '';
  const suffix = isPercent ? '%' : '';

  if (numberScale && numberScale !== 'none') {
    return `${prefix}${formatSelfServiceScaledNumber(scaledForFormat, decimals, numberScale)}${suffix}`;
  }

  switch (numberFormat) {
    case 'thousands':
      return scaledForFormat.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    case 'percent':
      return `${scaledForFormat.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}%`;
    case 'currency':
      return `${prefix}${scaledForFormat.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}`;
    // 'plain' / 'none' / anything unrecognised: show the number as the backend
    // sent it. `useGrouping: false` is the whole point — toLocaleString groups
    // by DEFAULT, so this branch used to emit "1,234,567" for the option
    // labelled "Plain (1234)" / "Default", making it indistinguishable from the
    // 'thousands' preset that exists precisely to add those commas.
    default:
      return scaledForFormat.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: false,
      });
  }
}

/** Abbreviate `value` to K/M/B. 'millions'/'billions' force that divisor;
 *  'compact' picks the divisor from the value's own magnitude. */
export function formatSelfServiceScaledNumber(value: number, decimals: number, numberScale: string): string {
  const abs = Math.abs(value);
  let scale = 1;
  let suffix = '';
  if (numberScale === 'billions') {
    scale = 1e9;
    suffix = 'B';
  } else if (numberScale === 'millions') {
    scale = 1e6;
    suffix = 'M';
  } else if (numberScale === 'thousands') {
    scale = 1e3;
    suffix = 'K';
  } else if (abs >= 1e9) {
    scale = 1e9;
    suffix = 'B';
  } else if (abs >= 1e6) {
    scale = 1e6;
    suffix = 'M';
  } else if (abs >= 1e3) {
    scale = 1e3;
    suffix = 'K';
  }
  return `${(value / scale).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;
}

export function snakeCaseToTitleCase(str: string): string {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ============ WRAPPER UTILITIES ============

/**
 * Round a value to a configurable number of decimal places.
 * Returns '-' for null/undefined/NaN.
 */
export function roundOff(value: any, decimals: number = 0): string {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toFixed(decimals);
}

/**
 * Wrap a formatted string value with an optional prefix and/or suffix.
 * Skips wrapping for null/dash placeholder values.
 */
export function wrapWithPrefixSuffix(
  formattedValue: string,
  prefix: string = '',
  suffix: string = ''
): string {
  if (formattedValue === '-' || formattedValue === '') return formattedValue;
  return `${prefix}${formattedValue}${suffix}`;
}

/**
 * Convert an API response key (snake_case or camelCase) to a
 * human-readable label with each word capitalized.
 * e.g., "total_sales" -> "Total Sales"
 *       "yearQuarter" -> "Year Quarter"
 */
export function capitalizeKey(key: string): string {
  if (!key) return '';
  let result = key.replace(/([a-z])([A-Z])/g, '$1 $2');
  result = result.replace(/[_-]/g, ' ');
  return result
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
